<template>
  <div class="auth-root">
    <main class="card">

      <!-- ESQUERDA — formulário -->
      <section class="form-side">
        <div class="logo" role="img" aria-label="StoneUP Negativa">
          <img class="logo__icone" src="../../assets/SVG/monitora-icone.svg" alt="" />
          <span class="logo__nome">Negativa</span>
        </div>

        <div class="form-slot">
          <slot />
        </div>

        <div class="form-side__footer" :class="{ 'form-side__footer--gap': rodapeEspacado }">© {{ ano }} StoneUP</div>
      </section>

      <!-- DIREITA — painel amarelo -->
      <aside class="visual">
        <auth-ilustracao class="visual__art" :nome="ilustracao" />

        <div class="slides">
          <div
            v-for="(slide, i) in slides"
            :key="i"
            class="slide"
            :class="{ 'is-active': i === slideAtual }"
            :aria-hidden="i !== slideAtual"
          >
            <h2 class="visual__title">{{ slide.titulo }}</h2>
            <p class="visual__sub">{{ slide.subtitulo }}</p>
          </div>
        </div>

        <div v-if="temCarrossel" class="dots">
          <button
            v-for="(slide, i) in slides"
            :key="i"
            type="button"
            class="dot"
            :class="{ 'is-active': i === slideAtual }"
            :aria-label="`Slide ${i + 1}`"
            @click="irParaSlide(i)"
          ></button>
        </div>
      </aside>
    </main>
  </div>
</template>

<script>
import AuthIlustracao from "./AuthIlustracao.vue";

const SLIDE_INTERVALO_MS = 5000;

/**
 * Layout v3 das telas de autenticação: card dividido, formulário à esquerda (slot)
 * e painel amarelo com ilustração + frases à direita.
 * Estilos do formulário ficam em `auth-form.css` (importado por cada tela).
 */
export default {
  name: "AuthLayout",
  components: { AuthIlustracao },
  props: {
    /** 'titulo' | 'cadastro' | 'email' | 'senha' | 'empresa' */
    ilustracao: { type: String, default: "titulo" },
    /** [{ titulo, subtitulo }] — com mais de uma, vira carrossel */
    slides: { type: Array, required: true },
    /** 24px entre o formulário e o rodapé (telas novas); o login v3 não tem esse espaço */
    rodapeEspacado: { type: Boolean, default: true },
  },
  data: () => ({
    slideAtual: 0,
    slideTimer: null,
    ano: new Date().getFullYear(),
  }),
  computed: {
    temCarrossel() {
      return this.slides.length > 1;
    },
  },
  mounted() {
    this.iniciarCarrossel();
  },
  beforeDestroy() {
    clearInterval(this.slideTimer);
  },
  methods: {
    iniciarCarrossel() {
      clearInterval(this.slideTimer);
      if (!this.temCarrossel) return;
      this.slideTimer = setInterval(() => {
        this.slideAtual = (this.slideAtual + 1) % this.slides.length;
      }, SLIDE_INTERVALO_MS);
    },

    /** Clique no dot: vai para o slide e reinicia o timer. */
    irParaSlide(indice) {
      this.slideAtual = indice;
      this.iniciarCarrossel();
    },
  },
};
</script>

<style scoped>
/* Página — anula o max-width/margens do .v-container global e cobre o fundo do App.vue */
.auth-root.auth-root {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100% !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  min-height: 100vh;
  background: #FFF6D1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  box-sizing: border-box;
  font-family: 'Inter', -apple-system, 'Segoe UI', sans-serif;
  color: #1e293b;
  /* O Vuetify aplica line-height 1.5 em .v-application; o protótipo usa o padrão do navegador */
  line-height: normal;
  -webkit-font-smoothing: antialiased;
}

.auth-root >>> *,
.auth-root >>> *::before,
.auth-root >>> *::after {
  box-sizing: border-box;
}

/* Card */
.card {
  width: 100%;
  max-width: 1080px;
  min-height: 620px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 12px 40px rgba(120, 90, 0, 0.10);
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;
}

/* ---------- Formulário (esquerda) ---------- */
.form-side {
  padding: 48px 64px;
  display: flex;
  flex-direction: column;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  align-self: flex-start;
}

.logo__icone {
  width: 52px;
  height: 52px;
}

.logo__nome {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1d232c;
  letter-spacing: 0.2px;
}

.form-slot {
  margin: auto 0;
  padding: 32px 0 8px;
  max-width: 380px;
  width: 100%;
}

.form-side__footer {
  font-size: 0.74rem;
  color: #94a3b8;
}
.form-side__footer--gap {
  margin-top: 24px;
}

/* ---------- Painel amarelo (direita) ---------- */
.visual {
  background: #FFD400;
  margin: 12px;
  border-radius: 14px;
  padding: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #003668;
}

.visual__art {
  width: 100%;
  max-width: 340px;
  height: auto;
  margin-bottom: 36px;
}

.slides {
  position: relative;
  min-height: 96px;
  width: 100%;
  max-width: 360px;
}

.slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.35s;
}
.slide.is-active {
  opacity: 1;
}

.visual__title {
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: -0.3px;
  line-height: normal;
  margin: 0 0 8px;
  color: #003668;
}

.visual__sub {
  font-size: 0.88rem;
  line-height: 1.5;
  margin: 0;
  color: rgba(0, 54, 104, 0.75);
}

.dots {
  display: flex;
  gap: 8px;
  margin-top: 28px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: rgba(0, 54, 104, 0.25);
  transition: width 0.2s, background 0.2s;
}
.dot.is-active {
  width: 24px;
  background: #003668;
}

/* ---------- Celular / tablet em pé ---------- */
@media (max-width: 860px) {
  .auth-root.auth-root {
    padding: 0;
    align-items: stretch;
    background: #fff;
  }
  .card {
    grid-template-columns: 1fr;
    border-radius: 0;
    box-shadow: none;
    min-height: 100vh;
  }
  .visual {
    display: none;
  }
  .form-side {
    padding: 40px 20px;
  }
}
</style>
